"use client";

import { useActionState, useEffect, useState } from "react";
import { useFormStatus } from "react-dom";
import { CheckCircle2, AlertCircle, Loader2, Send } from "lucide-react";
import { submitApplication, type FormResult } from "@/app/(site)/actions";
import { trackLead } from "@/lib/analytics";
import type { JobCard } from "@/lib/types";

const ROLES = [
  "エンジニア",
  "デザイナー",
  "営業",
  "カスタマーサクセス",
  "コーポレート",
  "その他 / 未定",
];

const initialState: FormResult | null = null;

async function action(
  _prev: FormResult | null,
  formData: FormData
): Promise<FormResult> {
  return submitApplication(formData);
}

function SubmitButton() {
  const { pending } = useFormStatus();
  return (
    <button type="submit" disabled={pending} className="btn-primary w-full disabled:opacity-60">
      {pending ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          送信中…
        </>
      ) : (
        <>
          応募する
          <Send className="h-4 w-4" aria-hidden />
        </>
      )}
    </button>
  );
}

const fieldLabel = "mb-1.5 block text-sm font-medium text-navy-900";
const fieldInput =
  "w-full rounded-lg border border-line bg-white px-4 py-3 text-[15px] text-ink outline-none transition-colors placeholder:text-navy-200 focus:border-navy-500 focus:ring-2 focus:ring-navy-100";

type Props = {
  /** 応募求人の選択肢（公開求人）。渡されると「応募求人」select を表示 */
  jobs?: JobCard[];
  /** 初期選択する求人の slug（求人詳細から遷移時） */
  defaultJobSlug?: string;
};

export default function EntryForm({ jobs, defaultJobSlug }: Props) {
  const [state, formAction] = useActionState(action, initialState);

  const hasJobs = !!jobs && jobs.length > 0;
  const initialSlug =
    hasJobs && jobs!.some((j) => j.slug === defaultJobSlug) ? defaultJobSlug! : "";
  const [selectedSlug, setSelectedSlug] = useState<string>(initialSlug);

  // defaultJobSlug は URL の ?job= から遅れて渡ってくる（EntrySection 参照）。
  // 初期値として読むだけだと後から来た指定が反映されないので、変化を拾う。
  // ユーザーが自分で選び直したあとに上書きしないよう、空のときだけ入れる。
  useEffect(() => {
    if (!defaultJobSlug) return;
    if (!jobs?.some((j) => j.slug === defaultJobSlug)) return;
    setSelectedSlug((cur) => (cur === "" ? defaultJobSlug : cur));
  }, [defaultJobSlug, jobs]);

  const selectedJob = hasJobs
    ? jobs!.find((j) => j.slug === selectedSlug) ?? null
    : null;

  // 送信成功時にリード獲得を計測（GA4 / Meta Pixel）
  useEffect(() => {
    if (state?.ok) trackLead("application");
  }, [state?.ok]);

  if (state?.ok) {
    return (
      <div className="rounded-xl2 border border-line bg-mist/60 p-10 text-center">
        <CheckCircle2 className="mx-auto h-12 w-12 text-navy-600" aria-hidden />
        <p className="mt-4 text-lg font-bold text-navy-900">送信が完了しました</p>
        <p className="mt-2 text-sm leading-relaxed text-muted">{state.message}</p>
      </div>
    );
  }

  return (
    <form action={formAction} className="flex flex-col gap-5" noValidate>
      {state && !state.ok && (
        <p
          role="alert"
          className="flex items-center gap-2 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden />
          {state.message}
        </p>
      )}

      {/* 選択中の求人 id を submitApplication に渡す（サーバ側で照合用） */}
      {selectedJob && <input type="hidden" name="job_id" value={selectedJob.id} />}

      <div>
        <label htmlFor="entry-name" className={fieldLabel}>
          お名前 <span className="text-accent-text">*</span>
        </label>
        <input
          id="entry-name"
          name="name"
          type="text"
          required
          autoComplete="name"
          placeholder="採用 太郎"
          className={fieldInput}
        />
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="entry-email" className={fieldLabel}>
            メールアドレス <span className="text-accent-text">*</span>
          </label>
          <input
            id="entry-email"
            name="email"
            type="email"
            required
            autoComplete="email"
            placeholder="you@example.com"
            className={fieldInput}
          />
        </div>
        <div>
          <label htmlFor="entry-phone" className={fieldLabel}>
            電話番号
          </label>
          <input
            id="entry-phone"
            name="phone"
            type="tel"
            autoComplete="tel"
            placeholder="090-0000-0000"
            className={fieldInput}
          />
        </div>
      </div>

      {/* 応募求人（公開求人がある場合のみ） */}
      {hasJobs && (
        <div>
          <label htmlFor="entry-job" className={fieldLabel}>
            応募求人
          </label>
          <select
            id="entry-job"
            className={fieldInput}
            value={selectedSlug}
            onChange={(e) => setSelectedSlug(e.target.value)}
          >
            <option value="">指定なし / 求人を選んでください</option>
            {jobs!.map((j) => (
              <option key={j.id} value={j.slug}>
                {j.title}
              </option>
            ))}
          </select>
        </div>
      )}

      <div>
        <label htmlFor="entry-role" className={fieldLabel}>
          希望職種
        </label>
        <select id="entry-role" name="desired_role" className={fieldInput} defaultValue="">
          <option value="" disabled>
            選択してください
          </option>
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="entry-message" className={fieldLabel}>
          メッセージ
        </label>
        <textarea
          id="entry-message"
          name="message"
          rows={5}
          placeholder="志望動機やご質問などをご自由にご記入ください。"
          className={`${fieldInput} resize-y`}
        />
      </div>

      <SubmitButton />
      <p className="text-xs leading-relaxed text-muted">
        送信いただいた内容は採用選考の目的にのみ利用します。
      </p>
    </form>
  );
}
